"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { CalendarRange, ChartLine as LineChart, ShieldAlert, ArrowUpRight, Flame, Sun, CloudRain, Sparkles } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
const challenges = [{
  id: 1,
  title: "Event-Driven Compression & Window Shifts",
  subtitle: "Demand management",
  iconName: "CalendarRange",
  description: "Manage massive surges during Dev Deepawali, Maha Shivratri, and Kartik Purnima against sharp shoulder lulls. We implement dynamic minimum length-of-stay (MLOS) rules and pace-based pricing so your property maximizes peak RevPAR without relying on panic discounting during quiet periods.",
  metricLabel: "Strategy focus",
  metricValue: "Event-driven MLOS, real-time pace pacing, & advance rate tiers"
}, {
  id: 2,
  title: "Aggressive Comp-Set Undercutting",
  subtitle: "Real-time rate intelligence",
  iconName: "LineChart",
  description: "Track intraday rate shifts across MakeMyTrip, Agoda, and Booking.com for Varanasi's dense ghats and Cantonment micro-markets. Automated competitive alerts and yield adjustments protect your ADR while securing high-intent cultural and pilgrimage bookings ahead of market drops.",
  metricLabel: "Strategy focus",
  metricValue: "Intraday OTA monitoring & micro-market rate fencing"
}, {
  id: 3,
  title: "The OTA Commission & Parity Trap",
  subtitle: "Channel distribution",
  iconName: "ShieldAlert",
  description: "Stop unauthorized offline agent rate leakage and reclaim profit margins lost to high OTA commissions. We enforce strict rate parity across meta-search, optimize Google Hotels, and drive direct guest conversions through high-intent booking incentives.",
  metricLabel: "Strategy focus",
  metricValue: "Wholesaler rate fencing & direct brand channel boost"
}];
const seasonalityPhases = [{
  season: "Peak Spiritual & Cultural (Oct – Mar)",
  badge: "Highest ADR & RevPAR",
  icon: Flame,
  drivers: "Dev Deepawali, Kartik Purnima, Maha Shivratri, winter spiritual tourism & inbound international travelers.",
  tactic: "Strict 2-3 night MLOS, premium non-refundable rates, early-bird fences, and dynamic peak pricing."
}, {
  season: "Auspicious & Wedding Shoulder (Apr – Jun)",
  badge: "Volume & Banqueting Yield",
  icon: Sun,
  drivers: "Auspicious wedding dates (muhurats), corporate pilgrimages, and domestic spiritual retreats.",
  tactic: "Unbundled room & meal packages, weekend rate fencing, and targeted family group yield controls."
}, {
  season: "Monsoon & Sawan Period (Jul – Sep)",
  badge: "Targeted Flash & Segment Yield",
  icon: CloudRain,
  drivers: "High footfall during holy Sawan month mixed with broader leisure shoulder dips.",
  tactic: "Sawan-specific weekday yield packages, value-add experiential inclusions, and OTA geo-targeted promotions."
}];
export default function Landscape() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {},
    show: {
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.15
      }
    }
  };
  const cardVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="hospitalitylandscapevaranasi" data-nav="light" className="relative bg-background py-20 md:py-28 text-foreground">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {/* Section Header */}
        <div className="mx-auto max-w-3xl text-center mb-16 md:mb-20">
          <Badge variant="outline" className="mb-4 border-secondary/40 text-secondary-foreground">
            Market Dynamics & Yield Protection
          </Badge>
          <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight text-foreground">
            Navigating the hospitality landscape of Varanasi
          </h2>
          <p className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed">
            Varanasi is one of India&apos;s most dynamic spiritual and heritage micro-markets, characterized by extreme festival compression, surging inbound cultural tourism, and intense OTA competition across Ghats and Cantonment belts. Sunil Singh builds disciplined, data-backed revenue strategies tailored specifically for boutique retreats, heritage havelis, and luxury city hotels.
          </p>
        </div>

        {/* 3-Column Card Grid */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
        once: true,
        amount: 0.15
      }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {challenges.map((item, index) => <motion.div key={item.id} data-index={index} variants={cardVariants} className="h-full flex">
              <Card className="flex flex-col justify-between w-full border border-border bg-card shadow-sm hover:shadow-md transition-[border-color,box-shadow] duration-300">
                <CardHeader className="gap-3">
                  <div className="flex items-center justify-between">
                    <div className="inline-flex size-12 items-center justify-center rounded-md bg-secondary/10 text-secondary-foreground">
                      {item.iconName === "CalendarRange" && <CalendarRange className="size-6 text-chart-2" />}
                      {item.iconName === "LineChart" && <LineChart className="size-6 text-chart-2" />}
                      {item.iconName === "ShieldAlert" && <ShieldAlert className="size-6 text-chart-2" />}
                    </div>
                    <span className="text-xs font-mono font-medium text-muted-foreground tracking-wider uppercase">
                      0{index + 1}
                    </span>
                  </div>
                  <div>
                    <span className="text-xs font-semibold uppercase tracking-wider text-secondary">
                      {item.subtitle}
                    </span>
                    <CardTitle className="font-serif text-xl sm:text-2xl font-bold mt-1.5 text-foreground leading-snug">
                      {item.title}
                    </CardTitle>
                  </div>
                </CardHeader>

                <CardContent className="space-y-6 pt-2">
                  <CardDescription className="text-sm leading-relaxed text-muted-foreground">
                    {item.description}
                  </CardDescription>

                  <div className="rounded-md border border-border/60 bg-muted/40 p-3.5 text-xs">
                    <p className="font-semibold text-foreground mb-1">
                      {item.metricLabel}
                    </p>
                    <p className="text-muted-foreground">
                      {item.metricValue}
                    </p>
                  </div>
                </CardContent>
              </Card>
            </motion.div>)}
        </motion.div>

        {/* Varanasi Seasonality & Demand Dynamics Block */}
        <motion.div initial={{
        opacity: 0,
        y: shouldReduceMotion ? 0 : 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        amount: 0.15
      }} transition={{
        duration: 0.5,
        ease: "easeOut"
      }} className="mt-16 md:mt-20 rounded-xl border border-border bg-card p-6 md:p-8 shadow-sm">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 pb-6 border-b border-border/70">
            <div>
              <div className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-secondary mb-1">
                <Sparkles className="size-3.5" />
                Varanasi Market Seasonality Framework
              </div>
              <h3 className="font-serif text-xl sm:text-2xl font-bold text-foreground">
                Year-Round Demand Cycles & Revenue Levers
              </h3>
            </div>
            <p className="text-sm text-muted-foreground max-w-md">
              How our revenue management engine captures high-yield opportunities across Varanasi&apos;s distinct spiritual and seasonal phases.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 pt-6">
            {seasonalityPhases.map((phase, idx) => {
            const IconComponent = phase.icon;
            return <div key={phase.season} data-index={idx} className="rounded-lg border border-border/60 bg-muted/20 p-5 flex flex-col justify-between">
                  <div>
                    <div className="flex items-center justify-between gap-2 mb-3">
                      <div className="inline-flex size-9 items-center justify-center rounded-md bg-secondary/15 text-secondary-foreground">
                        <IconComponent className="size-4 text-chart-2" />
                      </div>
                      <Badge variant="secondary" className="text-[11px] font-medium bg-secondary/15 text-secondary-foreground">
                        {phase.badge}
                      </Badge>
                    </div>
                    <h4 className="text-base font-bold text-foreground mb-2">
                      {phase.season}
                    </h4>
                    <p className="text-xs text-muted-foreground mb-4 leading-relaxed">
                      <strong className="text-foreground">Drivers:</strong> {phase.drivers}
                    </p>
                  </div>
                  <div className="rounded-md border border-border/50 bg-background/80 p-3 text-xs">
                    <span className="font-semibold text-foreground block mb-0.5">Revenue Lever:</span>
                    <span className="text-muted-foreground">{phase.tactic}</span>
                  </div>
                </div>;
          })}
          </div>
        </motion.div>

        {/* Section Bottom CTA / Footnote */}
        <div className="mt-14 md:mt-18 pt-8 border-t border-border/70 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-sm text-muted-foreground text-center sm:text-left">
            Need a tailored revenue assessment for your Varanasi hotel or boutique stay?
          </p>
          <Button asChild variant="default" className="bg-primary text-primary-foreground hover:bg-primary/90">
            <Link to="#" className="inline-flex items-center gap-2">
              <span>Book a Revenue Audit</span>
              <ArrowUpRight className="size-4" />
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}